import React, { useState } from 'react';
import { Plus, ArrowUpRight, ArrowDownRight, Search, Filter, Download, CreditCard, Building } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useAppContext } from '../contexts/AppContext';
import { formatCurrency, formatDate } from '../utils/formatters';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';
import { toast } from 'sonner';
const COLORS = ['#73C883', '#FFB347', '#3b82f6', '#8b5cf6', '#f43f5e'];
export const Wallet: React.FC = () => {
  const {
    state,
    dispatch
  } = useAppContext();
  const [activeTab, setActiveTab] = useState<'all' | 'in' | 'out'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  // Modals
  const [isAddFundsOpen, setIsAddFundsOpen] = useState(false);
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  // Form states
  const [fundAmount, setFundAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  // Filter transactions
  const filteredTransactions = state.transactions.filter(tx => {
    const matchesTab = activeTab === 'all' ? true : activeTab === 'in' ? tx.amount > 0 : tx.amount < 0;
    const matchesSearch = tx.title.toLowerCase().includes(searchQuery.toLowerCase()) || tx.reference.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'All' ? true : tx.type === typeFilter;
    return matchesTab && matchesSearch && matchesType;
  });
  // Chart Data (Spending by Category)
  const spendingByCategory = state.transactions.filter(tx => tx.amount < 0).reduce((acc, tx) => {
    acc[tx.type] = (acc[tx.type] || 0) + Math.abs(tx.amount);
    return acc;
  }, {} as Record<string, number>);
  const chartData = Object.entries(spendingByCategory).map(([name, value]) => ({
    name,
    value
  }));
  const handleAddFunds = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(fundAmount);
    if (amount < 1000) {
      toast.error('Minimum amount is ₦1,000');
      return;
    }
    dispatch({
      type: 'ADD_FUNDS',
      payload: amount
    });
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: {
        id: `tx_${Date.now()}`,
        type: 'Wallet',
        title: 'Card Funding',
        amount: amount,
        status: 'Completed',
        date: new Date().toISOString(),
        reference: `FND-${Date.now().toString().slice(-6)}`
      }
    });
    toast.success(`Successfully added ${formatCurrency(amount)}`);
    setIsAddFundsOpen(false);
    setFundAmount('');
  };
  const handleWithdraw = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(withdrawAmount);
    if (amount < 1000) {
      toast.error('Minimum withdrawal is ₦1,000');
      return;
    }
    if (amount > (state.user?.walletBalance || 0)) {
      toast.error('Insufficient wallet balance');
      return;
    }
    if (!bankAccount) {
      toast.error('Please select a bank account');
      return;
    }
    dispatch({
      type: 'DEDUCT_FUNDS',
      payload: amount
    });
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: {
        id: `tx_${Date.now()}`,
        type: 'Wallet',
        title: `Withdrawal to ${bankAccount}`,
        amount: -amount,
        status: 'Completed',
        date: new Date().toISOString(),
        reference: `WDL-${Date.now().toString().slice(-6)}`
      }
    });
    toast.success(`Successfully withdrew ${formatCurrency(amount)}`);
    setIsWithdrawOpen(false);
    setWithdrawAmount('');
    setBankAccount('');
  };
  return <div className="space-y-6" data-id="element-983">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-984">
        <div data-id="element-985">
          <h1 className="text-2xl font-bold text-brand-text-dark" data-id="element-986">
            Wallet & Transactions
          </h1>
          <p className="text-brand-text-light text-sm" data-id="element-987">
            Manage your funds and view transaction history.
          </p>
        </div>
        <div className="flex gap-3 shrink-0" data-id="element-988">
          <button onClick={() => setIsWithdrawOpen(true)} className="px-5 py-2.5 bg-white border border-gray-200 text-brand-text-dark rounded-xl font-semibold hover:bg-gray-50 transition-colors flex items-center gap-2" data-id="element-989">
            <ArrowUpRight size={20} data-id="element-990" /> Withdraw
          </button>
          <button onClick={() => setIsAddFundsOpen(true)} className="bg-brand-primary hover:bg-brand-primary-dark text-white px-5 py-2.5 rounded-xl font-semibold transition-colors flex items-center gap-2" data-id="element-991">
            <Plus size={20} data-id="element-992" /> Add Funds
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-993">
        {/* Balance Card */}
        <div className="lg:col-span-2 bg-brand-primary-dark rounded-3xl p-8 text-white relative overflow-hidden shadow-lg shadow-brand-primary-dark/20 flex flex-col justify-center" data-id="element-994">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-995" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-primary/20 rounded-full blur-2xl translate-y-1/3 -translate-x-1/4" data-id="element-996" />

          <div className="relative z-10" data-id="element-997">
            <p className="text-brand-primary-light font-medium mb-2" data-id="element-998">
              Available Balance
            </p>
            <h2 className="text-5xl font-bold tracking-tight mb-6" data-id="element-999">
              {formatCurrency(state.user?.walletBalance || 0)}
            </h2>
            <div className="flex items-center gap-4 text-sm font-medium" data-id="element-1000">
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg backdrop-blur-sm" data-id="element-1001">
                <ArrowDownRight size={16} className="text-brand-primary-light" data-id="element-1002" />
                <span data-id="element-1003">
                  In:{' '}
                  {formatCurrency(state.transactions.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0))}
                </span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg backdrop-blur-sm" data-id="element-1004">
                <ArrowUpRight size={16} className="text-brand-accent" data-id="element-1005" />
                <span data-id="element-1006">
                  Out:{' '}
                  {formatCurrency(Math.abs(state.transactions.filter(t => t.amount < 0).reduce((s, t) => s + t.amount, 0)))}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Spending Chart */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 flex flex-col" data-id="element-1007">
          <h3 className="font-semibold text-brand-text-dark mb-4" data-id="element-1008">
            Spending by Category
          </h3>
          {chartData.length > 0 ? <div className="flex-1 min-h-[200px]" data-id="element-1009">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1010">
                <PieChart data-id="element-1011">
                  <Pie data={chartData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-1012">
                    {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-1013" />)}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-1014" />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" data-id="element-1015" />
                </PieChart>
              </ResponsiveContainer>
            </div> : <div className="flex-1 flex items-center justify-center text-gray-400 text-sm" data-id="element-1016">
              No spending data yet
            </div>}
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-1017">
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1018">
          <h3 className="text-lg font-semibold text-brand-text-dark" data-id="element-1019">
            Transaction History
          </h3>

          <div className="flex flex-col sm:flex-row gap-3" data-id="element-1020">
            <div className="relative" data-id="element-1021">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-id="element-1022" />
              <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary w-full sm:w-48 transition-all" data-id="element-1023" />
            </div>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-1024">
              <option value="All" data-id="element-1025">All Types</option>
              <option value="Wallet" data-id="element-1026">Wallet</option>
              <option value="Diaspora" data-id="element-1027">Diaspora</option>
              <option value="Bills" data-id="element-1028">Bills</option>
              <option value="Crowdfunding" data-id="element-1029">Crowdfunding</option>
              <option value="Facility" data-id="element-1030">Facility</option>
            </select>
            <button className="p-2 text-gray-500 hover:text-brand-text-dark bg-gray-50 hover:bg-gray-100 rounded-xl border border-gray-200 transition-colors" title="Export CSV" data-id="element-1031">
              <Download size={18} data-id="element-1032" />
            </button>
          </div>
        </div>

        <div className="flex space-x-1 bg-gray-50 p-1 border-b border-gray-100" data-id="element-1033">
          {(['all', 'in', 'out'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${activeTab === tab ? 'bg-white text-brand-text-dark shadow-sm' : 'text-brand-text-light hover:text-brand-text-dark hover:bg-white/50'}`} data-id="element-1034">
              {tab === 'all' ? 'All Transactions' : tab === 'in' ? 'Money In' : 'Money Out'}
            </button>)}
        </div>

        {filteredTransactions.length > 0 ? <div className="overflow-x-auto" data-id="element-1035">
            <table className="w-full text-left text-sm" data-id="element-1036">
              <thead className="bg-white text-brand-text-light border-b border-gray-100" data-id="element-1037">
                <tr data-id="element-1038">
                  <th className="px-6 py-4 font-medium" data-id="element-1039">Transaction</th>
                  <th className="px-6 py-4 font-medium" data-id="element-1040">Type</th>
                  <th className="px-6 py-4 font-medium" data-id="element-1041">Date</th>
                  <th className="px-6 py-4 font-medium" data-id="element-1042">Reference</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-1043">Amount</th>
                  <th className="px-6 py-4 font-medium text-center" data-id="element-1044">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50" data-id="element-1045">
                {filteredTransactions.map(tx => <tr key={tx.id} className="hover:bg-gray-50/50 transition-colors group" data-id="element-1046">
                    <td className="px-6 py-4" data-id="element-1047">
                      <div className="flex items-center gap-3" data-id="element-1048">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${tx.amount > 0 ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-1049">
                          {tx.amount > 0 ? <ArrowDownRight size={20} data-id="element-1050" /> : <ArrowUpRight size={20} data-id="element-1051" />}
                        </div>
                        <span className="font-medium text-brand-text-dark" data-id="element-1052">
                          {tx.title}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-brand-text-light" data-id="element-1053">
                      {tx.type}
                    </td>
                    <td className="px-6 py-4 text-brand-text-light" data-id="element-1054">
                      {formatDate(tx.date)}
                    </td>
                    <td className="px-6 py-4 text-brand-text-light font-mono text-xs" data-id="element-1055">
                      {tx.reference}
                    </td>
                    <td className={`px-6 py-4 font-bold text-right ${tx.amount > 0 ? 'text-green-600' : 'text-brand-text-dark'}`} data-id="element-1056">
                      {tx.amount > 0 ? '+' : ''}
                      {formatCurrency(tx.amount)}
                    </td>
                    <td className="px-6 py-4 text-center" data-id="element-1057">
                      <StatusBadge status={tx.status} data-id="element-1058" />
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div> : <div className="p-12" data-id="element-1059">
            <EmptyState icon={Search} title="No transactions found" description="Try adjusting your filters or search query." data-id="element-1060" />
          </div>}
      </div>

      {/* Add Funds Modal */}
      <Modal isOpen={isAddFundsOpen} onClose={() => setIsAddFundsOpen(false)} title="Add Funds to Wallet" data-id="element-1061">
        <form onSubmit={handleAddFunds} className="space-y-4" data-id="element-1062">
          <div data-id="element-1063">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1064">
              Amount (₦)
            </label>
            <input type="number" value={fundAmount} onChange={e => setFundAmount(e.target.value)} placeholder="Enter amount" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-lg font-semibold" min="1000" required data-id="element-1065" />
          </div>

          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3" data-id="element-1066">
            <p className="text-sm font-medium text-gray-700" data-id="element-1067">Payment Method</p>
            <div className="flex items-center gap-3 p-3 bg-white border border-brand-primary rounded-lg shadow-sm" data-id="element-1068">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600" data-id="element-1069">
                <CreditCard size={20} data-id="element-1070" />
              </div>
              <div className="flex-1" data-id="element-1071">
                <p className="text-sm font-medium text-brand-text-dark" data-id="element-1072">
                  Debit Card
                </p>
                <p className="text-xs text-brand-text-light" data-id="element-1073">•••• 4242</p>
              </div>
              <div className="w-4 h-4 rounded-full border-4 border-brand-primary bg-white" data-id="element-1074"></div>
            </div>
          </div>

          <button type="submit" className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3.5 rounded-xl font-semibold transition-colors mt-4 shadow-md" data-id="element-1075">
            Fund Wallet
          </button>
        </form>
      </Modal>

      {/* Withdraw Modal */}
      <Modal isOpen={isWithdrawOpen} onClose={() => setIsWithdrawOpen(false)} title="Withdraw Funds" data-id="element-1076">
        <form onSubmit={handleWithdraw} className="space-y-4" data-id="element-1077">
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 mb-4 flex justify-between items-center" data-id="element-1078">
            <span className="text-sm text-brand-text-light" data-id="element-1079">
              Available Balance
            </span>
            <span className="font-bold text-brand-text-dark text-lg" data-id="element-1080">
              {formatCurrency(state.user?.walletBalance || 0)}
            </span>
          </div>

          <div data-id="element-1081">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1082">
              Amount (₦)
            </label>
            <input type="number" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} placeholder="Enter amount" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-lg font-semibold" min="1000" required data-id="element-1083" />
          </div>

          <div data-id="element-1084">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1085">
              Destination Bank
            </label>
            <select required value={bankAccount} onChange={e => setBankAccount(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-1086">
              <option value="" disabled data-id="element-1087">
                Select saved account
              </option>
              <option value="GTBank - 0123456789" data-id="element-1088">GTBank - 0123456789</option>
              <option value="Access - 0987654321" data-id="element-1089">Access - 0987654321</option>
            </select>
          </div>

          <button type="submit" className="w-full bg-brand-text-dark hover:bg-black text-white py-3.5 rounded-xl font-semibold transition-colors mt-4 shadow-md flex items-center justify-center gap-2" data-id="element-1090">
            <Building size={18} data-id="element-1091" /> Withdraw to Bank
          </button>
        </form>
      </Modal>
    </div>;
};